import { useEffect, useState } from 'react';
import { useStore } from '../state/store';

export function SessionPicker() {
  const {
    sessions,
    current,
    refreshSessions,
    openSession,
    createSession,
    renameSession,
    deleteSession,
    deleteAllSessions,
    toast,
  } = useStore((s) => ({
    sessions: s.sessions,
    current: s.current,
    refreshSessions: s.refreshSessions,
    openSession: s.openSession,
    createSession: s.createSession,
    renameSession: s.renameSession,
    deleteSession: s.deleteSession,
    deleteAllSessions: s.deleteAllSessions,
    toast: s.toast,
  }));
  const [name, setName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  useEffect(() => { void refreshSessions(); }, [refreshSessions]);

  async function onCreate() {
    const trimmed = name.trim() || `Capture ${new Date().toISOString().slice(0, 16)}`;
    await createSession(trimmed);
    setName('');
  }

  function startRename(id: string, currentName: string, e: React.MouseEvent) {
    e.stopPropagation();
    setRenamingId(id);
    setRenameDraft(currentName);
  }

  async function commitRename(id: string) {
    const trimmed = renameDraft.trim();
    setRenamingId(null);
    if (trimmed && trimmed !== sessions.find((s) => s.id === id)?.name) {
      await renameSession(id, trimmed);
    }
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameDraft('');
  }

  async function onDeleteOne(id: string, sessionName: string, rowCount: number, e: React.MouseEvent) {
    e.stopPropagation();
    const ok = window.confirm(
      `Delete "${sessionName}"?\n\n` +
      `This permanently removes the session and its ${rowCount} row${rowCount === 1 ? '' : 's'}. ` +
      `This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await deleteSession(id);
      toast('success', `Deleted "${sessionName}"`);
    } catch (err) {
      toast('error', `Could not delete: ${(err as Error).message}`);
    }
  }

  async function onDeleteAll() {
    const total = sessions.length;
    if (total === 0) return;
    const totalRows = sessions.reduce((acc, s) => acc + s.rowCount, 0);
    const first = window.confirm(
      `Delete ALL ${total} sessions?\n\n` +
      `This permanently removes every session you own and ${totalRows} ` +
      `row${totalRows === 1 ? '' : 's'} in total. This cannot be undone.`,
    );
    if (!first) return;
    // Require a typed confirmation as a second guard for the bulk action.
    const typed = window.prompt('Type DELETE ALL to confirm.', '');
    if (typed?.trim().toUpperCase() !== 'DELETE ALL') {
      toast('warn', 'Bulk delete cancelled');
      return;
    }
    try {
      const deleted = await deleteAllSessions();
      toast('success', `Deleted ${deleted} session${deleted === 1 ? '' : 's'}`);
    } catch (err) {
      toast('error', `Could not delete all: ${(err as Error).message}`);
    }
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <span>Sessions</span>
        {sessions.length > 0 && (
          <button
            className="sidebar-delete-all"
            title="Delete every session you own (irreversible)"
            onClick={() => void onDeleteAll()}
          >
            Delete all
          </button>
        )}
      </div>
      <div className="sidebar-new">
        <input
          placeholder="New session name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void onCreate(); }}
        />
        <button onClick={() => void onCreate()}>+ New</button>
      </div>
      <ul className="sidebar-list">
        {sessions.length === 0 && <li className="sidebar-empty">No sessions yet.</li>}
        {sessions.map((s) => (
          <li
            key={s.id}
            className={current?.id === s.id ? 'active' : ''}
            onClick={() => {
              if (renamingId === s.id) return;
              void openSession(s.id);
            }}
          >
            {renamingId === s.id ? (
              <input
                className="sidebar-rename-input"
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={() => void commitRename(s.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void commitRename(s.id);
                  if (e.key === 'Escape') cancelRename();
                }}
              />
            ) : (
              <div className="sidebar-name-row">
                <div className="sidebar-name">{s.name}</div>
                <button
                  className="sidebar-row-btn"
                  title="Rename session"
                  onClick={(e) => startRename(s.id, s.name, e)}
                >
                  ✎
                </button>
                <button
                  className="sidebar-row-btn sidebar-row-btn--danger"
                  title="Delete session"
                  onClick={(e) => void onDeleteOne(s.id, s.name, s.rowCount, e)}
                >
                  🗑
                </button>
              </div>
            )}
            <div className="sidebar-meta">{s.rowCount} rows · {new Date(s.updatedAt).toLocaleString('en-GB')}</div>
          </li>
        ))}
      </ul>
    </aside>
  );
}
